import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import {
  Box,
  Paper,
  Button,
  Typography,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  useTheme,
  Alert
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useCustomTranslation } from "../../hooks/useCustomTranslation";
import { useApiClientMutation, useApiClientQuery } from "../../hooks/useApiClient";
import api_endpoints from "../../api";
import useFundraiserSession from "../../hooks/useFundraiserSession";
import LanguageSwitcher from "../Login/LanguageSwitcher";
import {
  EMAIL_REGEX,
  normalizeDonorEmail,
  translateFundraiserError,
  cloverSdkUrl,
  forteJsUrl,
  loadScript,
  calculateSurcharge,
  calculateTotal,
  buildPaymentPayload,
  createPaymentIntentId,
} from "./shared";

// Import modular components
import {
  EmailVerification,
  DonorRegistration,
  DonorProfile,
  PaymentMethodsList,
  CardPaymentForm,
  BankPaymentForm,
  PromiseForm,
  PaymentPreview,
  DonationSuccess,
  ExistingPromises,
  ExistingPromisePayment,
} from "./components";

/**
 * MobileForm - Mobile-optimized version of the donor fundraiser flow
 * 
 * Same business logic as DesktopForm but with mobile-friendly UI
 */

// Steps enum for cleaner code
const STEPS = {
  EMAIL_VERIFICATION: "email_verification",
  DONOR_REGISTRATION: "donor_registration",
  DONOR_PROFILE: "donor_profile",
  PROMISE_SELECTION: "promise_selection",
  EXISTING_PROMISE_PAYMENT: "existing_promise_payment",
  PROMISE_FORM: "promise_form",
  PAYMENT_METHOD_SELECTION: "payment_method_selection",
  NEW_PAYMENT_METHOD: "new_payment_method",
  PREVIEW: "preview",
  SUCCESS: "success",
};

const MobileForm = ({ fundraiserData }) => {
  const { t } = useCustomTranslation("DonorFundraiser");
  const theme = useTheme();
  const { campaign, organization, url: fundraiserToken } = fundraiserData;
  const requestedPromiseId = useMemo(
    () => new URLSearchParams(window.location.search).get("promise") || "",
    []
  );

  // ========== Storage Key for Persistence ==========
  const storageKey = `fundraiser_form_${fundraiserToken}${requestedPromiseId ? `_promise_${requestedPromiseId}` : ""}`;

  // ========== Load Saved State ==========
  const loadSavedState = () => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (!saved) return null;

      const parsedState = JSON.parse(saved);
      const sessionExpiresAt = Number(parsedState.fundraiserSessionExpiresAt || 0);

      if ((parsedState.otpVerified && !parsedState.fundraiserSessionToken) || (sessionExpiresAt && sessionExpiresAt <= Date.now())) {
        localStorage.removeItem(storageKey);
        return null;
      }

      return parsedState;
    } catch (error) {
      console.error("Error loading saved fundraiser state:", error);
      return null;
    }
  };

  // ========== Save State to Storage ==========
  const saveState = (state) => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(state));
    } catch (error) {
      console.error("Error saving fundraiser state:", error);
    }
  };

  // ========== Clear Saved State ==========
  const clearSavedState = () => {
    try {
      const storagePrefix = `fundraiser_form_${fundraiserToken}`;
      Object.keys(localStorage)
        .filter((key) => key.startsWith(storagePrefix))
        .forEach((key) => localStorage.removeItem(key));
    } catch (error) {
      console.warn("Failed to clear fundraiser form state", error);
    }
  };

  // ========== Initialize State from Storage ==========
  const savedState = loadSavedState();

  // ========== State Initialization ==========
  const [currentStep, setCurrentStep] = useState(STEPS.EMAIL_VERIFICATION);
  const [stepHistory, setStepHistory] = useState([STEPS.EMAIL_VERIFICATION]);

  // ========== Email & OTP State ==========
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const {
    fundraiserSessionToken,
    fundraiserSessionTokenRef,
    fundraiserSessionExpiresAt,
    setFundraiserSession,
    clearFundraiserSession,
    setExpiryHandler,
  } = useFundraiserSession();
  const [isNewDonor, setIsNewDonor] = useState(false);

  // ========== Donor State ==========
  const [donorData, setDonorData] = useState(null);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    phone: "",
    address: "",
    zip_code: "",
  });
  const [savedPaymentMethods, setSavedPaymentMethods] = useState({
    cards: [],
    banks: [],
  });

  // ========== Promise State ==========
  const [promiseData, setPromiseData] = useState(null);
  const [existingPromises, setExistingPromises] = useState([]);
  const [selectedExistingPromise, setSelectedExistingPromise] = useState(null);
  const [amount, setAmount] = useState("");
  const [paymentType, setPaymentType] = useState("one_time");
  const [frequency, setFrequency] = useState("monthly");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState(null);
  const [numberOfPayments, setNumberOfPayments] = useState(null);
  const [perPaymentAmount, setPerPaymentAmount] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(campaign?._id || "");
  const [notes, setNotes] = useState("");
  const [surchargeEnabled, setSurchargeEnabled] = useState(false);
  const [promiseOnly, setPromiseOnly] = useState(false);

  // ========== Payment Method State ==========
  const [paymentMethod, setPaymentMethod] = useState("card");
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState(null);
  const [addingNewPaymentMethod, setAddingNewPaymentMethod] = useState(false);

  // ========== Card Tokenization State ==========
  const [cardTokenized, setCardTokenized] = useState(false);
  const [cardTokenData, setCardTokenData] = useState(null);
  const [paymentTokenId, setPaymentTokenId] = useState(null);
  const [paymentMethodId, setPaymentMethodId] = useState(null);
  const [cardLast4, setCardLast4] = useState("");
  const [cardBrand, setCardBrand] = useState("");

  // ========== ACH Tokenization State ==========
  const [achTokenized, setAchTokenized] = useState(false);
  const [bankName, setBankName] = useState("");
  const [bankLast4, setBankLast4] = useState("");
  const [accountHolderName, setAccountHolderName] = useState("");

  // ========== Transaction State ==========
  const [transactionId, setTransactionId] = useState(null);
  const [promiseId, setPromiseId] = useState(null);
  const [submitError, setSubmitError] = useState(null);

  // ========== Restore State from localStorage ==========
  useEffect(() => {
    if (savedState) {
      setCurrentStep(savedState.currentStep || STEPS.EMAIL_VERIFICATION);
      setStepHistory(savedState.stepHistory || [STEPS.EMAIL_VERIFICATION]);
      setEmail(savedState.email || "");
      setOtp(savedState.otp || "");
      setOtpSent(savedState.otpSent || false);
      setOtpVerified(savedState.otpVerified || false);
      setFundraiserSession(
        savedState.fundraiserSessionToken || "",
        savedState.fundraiserSessionExpiresAt || null
      );
      setIsNewDonor(savedState.isNewDonor || false);
      setDonorData(savedState.donorData || null);
      setFormData(savedState.formData || {
        first_name: "",
        last_name: "",
        phone: "",
        address: "",
        zip_code: "",
      });
      setSavedPaymentMethods(savedState.savedPaymentMethods || {
        cards: [],
        banks: [],
      });
      setPromiseData(savedState.promiseData || null);
      setExistingPromises(savedState.existingPromises || []);
      setSelectedExistingPromise(savedState.selectedExistingPromise || null);
      setAmount(savedState.amount || "");
      setPaymentType(savedState.paymentType || "one_time");
      setFrequency(savedState.frequency || "monthly");
      setStartDate(savedState.startDate || "");
      setEndDate(savedState.endDate || null);
      setNumberOfPayments(savedState.numberOfPayments || null);
      setPerPaymentAmount(savedState.perPaymentAmount || null);
      setSelectedCampaign(savedState.selectedCampaign || campaign?._id || "");
      setNotes(savedState.notes || "");
      setSurchargeEnabled(savedState.surchargeEnabled || false);
      setPromiseOnly(savedState.promiseOnly || false);
      setPaymentMethod(savedState.paymentMethod || "card");
      setSelectedPaymentMethodId(savedState.selectedPaymentMethodId || null);
      setAddingNewPaymentMethod(savedState.addingNewPaymentMethod || false);
      setCardTokenized(savedState.cardTokenized || false);
      setCardTokenData(savedState.cardTokenData || null);
      setPaymentTokenId(savedState.paymentTokenId || null);
      setPaymentMethodId(savedState.paymentMethodId || null);
      setCardLast4(savedState.cardLast4 || "");
      setCardBrand(savedState.cardBrand || "");
      setAchTokenized(savedState.achTokenized || false);
      setBankName(savedState.bankName || "");
      setBankLast4(savedState.bankLast4 || "");
      setAccountHolderName(savedState.accountHolderName || "");
      setTransactionId(savedState.transactionId || null);
      setPromiseId(savedState.promiseId || null);
      setSubmitError(savedState.submitError || null);
    }
  }, []); // Empty dependency array - only run once on mount

  // ========== SDK State ==========
  const cloverRef = useRef(null);
  const cloverElements = useRef({});
  const cloverMounted = useRef(false);
  const paymentIntentRef = useRef({ fingerprint: null, id: null });
  const promisesRequestVersionRef = useRef(0);
  const otpVerificationVersionRef = useRef(0);
  const [cloverReady, setCloverReady] = useState(false);
  const [cloverLoading, setCloverLoading] = useState(false);
  const [cloverError, setCloverError] = useState("");
  const [forteReady, setForteReady] = useState(false);
  const [forteLoading, setForteLoading] = useState(false);
  const [forteError, setForteError] = useState("");

  // ========== Payment Config ==========
  const [cardPaymentAvailable, setCardPaymentAvailable] = useState(true);
  // ACH/Forte is temporarily disabled in production.
  // Keep bank payment method unavailable until PROD Forte env is ready.
  const [achPaymentAvailable, setAchPaymentAvailable] = useState(false);

  // ========== Computed Values ==========
  const surchargeValue = useMemo(() => {
    return calculateSurcharge(
      surchargeEnabled,
      amount,
      paymentMethod,
      organization
    );
  }, [surchargeEnabled, amount, paymentMethod, organization]);

  const totalAmount = useMemo(() => {
    return calculateTotal(amount, surchargeEnabled, surchargeValue);
  }, [amount, surchargeEnabled, surchargeValue]);

  // ========== Save State to localStorage ==========
  useEffect(() => {
    const stateToSave = {
      currentStep,
      stepHistory,
      email,
      otp,
      otpSent,
      otpVerified,
      fundraiserSessionToken,
      fundraiserSessionExpiresAt,
      isNewDonor,
      donorData,
      formData,
      savedPaymentMethods,
      promiseData,
      existingPromises,
      selectedExistingPromise,
      amount,
      paymentType,
      frequency,
      startDate,
      endDate,
      numberOfPayments,
      perPaymentAmount,
      selectedCampaign,
      notes,
      surchargeEnabled,
      promiseOnly,
      paymentMethod,
      selectedPaymentMethodId,
      addingNewPaymentMethod,
      cardTokenized,
      cardTokenData,
      paymentTokenId,
      paymentMethodId,
      cardLast4,
      cardBrand,
      achTokenized,
      bankName,
      bankLast4,
      accountHolderName,
      transactionId,
      promiseId,
    };
    saveState(stateToSave);
  }, [
    currentStep,
    stepHistory,
    email,
    otp,
    otpSent,
    otpVerified,
    fundraiserSessionToken,
    fundraiserSessionExpiresAt,
    isNewDonor,
    donorData,
    formData,
    savedPaymentMethods,
    promiseData,
    existingPromises,
    selectedExistingPromise,
    amount,
    paymentType,
    frequency,
    startDate,
    endDate,
    numberOfPayments,
    perPaymentAmount,
    selectedCampaign,
    notes,
    surchargeEnabled,
    promiseOnly,
    paymentMethod,
    selectedPaymentMethodId,
    addingNewPaymentMethod,
    cardTokenized,
    cardTokenData,
    paymentTokenId,
    paymentMethodId,
    cardLast4,
    cardBrand,
    achTokenized,
    bankName,
    bankLast4,
    accountHolderName,
    transactionId,
    promiseId,
  ]);

  // ========== Clear State on Success ==========
  useEffect(() => {
    if (currentStep === STEPS.SUCCESS) {
      clearSavedState();
    }
  }, [currentStep]);

  // ========== API Mutations ==========
  const sendOtpMutation = useApiClientMutation(
    (body) => api_endpoints.sendFundraiserOTP(fundraiserToken, body),
    [],
    () => {
      setOtp("");
      setOtpSent(true);
    }
  );

  const applyVerifiedOtpResponse = (data) => {
    const promisesRequestVersion = ++promisesRequestVersionRef.current;
    setOtpVerified(true);
    setFundraiserSession(data.sessionToken, data.sessionExpiresAt);
    setIsNewDonor(data.isNewDonor);
    if (!data.isNewDonor && data.donor) {
      setDonorData(data.donor);
      setFormData({
        first_name: data.donor.first_name || "",
        last_name: data.donor.last_name || "",
        phone: data.donor.phone || "",
        address: data.donor.adresse || "",
        zip_code: data.donor.zip_code || "",
      });
      if (data.donor.cards) setSavedPaymentMethods(prev => ({ ...prev, cards: data.donor.cards }));
      if (data.donor.banks) setSavedPaymentMethods(prev => ({ ...prev, banks: data.donor.banks }));
      setExistingPromises(Array.isArray(data.promises) ? data.promises : []);
      api_endpoints
        .getFundraiserPromises(fundraiserToken, data.sessionToken)
        .then((response) => {
          if (promisesRequestVersion !== promisesRequestVersionRef.current) return;
          setExistingPromises(Array.isArray(response.data) ? response.data : []);
        })
        .catch((error) => {
          if (promisesRequestVersion !== promisesRequestVersionRef.current) return;
          console.error("Failed to load existing promises:", error);
        });
    } else {
      setExistingPromises([]);
    }
  };

  const verifyOtpMutation = useApiClientMutation(
    (body) => api_endpoints.verifyFundraiserOTP(fundraiserToken, body),
    []
  );

  const createDonorMutation = useApiClientMutation(
    (body) => api_endpoints.createFundraiserDonor(fundraiserToken, body, fundraiserSessionTokenRef.current),
    [],
    (data) => {
      setDonorData(data.donor);
      setIsNewDonor(false);
      if (Array.isArray(data?.donor?.cards)) {
        setSavedPaymentMethods((prev) => ({ ...prev, cards: data.donor.cards }));
      }
      if (Array.isArray(data?.donor?.banks)) {
        setSavedPaymentMethods((prev) => ({ ...prev, banks: data.donor.banks }));
      }
    }
  );

  const updateDonorMutation = useApiClientMutation(
    (body) => api_endpoints.updateFundraiserDonor(fundraiserToken, body, fundraiserSessionTokenRef.current),
    [],
    (data) => {
      setDonorData(data.donor);
    }
  );

  const createPromiseMutation = useApiClientMutation(
    (body) => api_endpoints.createFundraiserPromise(fundraiserToken, body, fundraiserSessionTokenRef.current),
    [],
    (data) => {
      setPromiseData(data.promise);
      setPromiseId(data.promise?._id);
    }
  );

  const tokenizeCardMutation = useApiClientMutation(
    (body) => api_endpoints.tokenizeFundraiserCard(fundraiserToken, body, fundraiserSessionTokenRef.current),
    [],
    (data) => {
      setCardTokenized(true);
      setPaymentTokenId(data.paymentTokenId);
      setPaymentMethodId(data.donorCardId);
      setCardLast4(data.last4);
      setCardBrand(data.brand);
      setSavedPaymentMethods((prev) => {
        const existingCards = Array.isArray(prev?.cards) ? prev.cards : [];
        const updatedCard = {
          _id: data.donorCardId,
          brand: data.brand || "Card",
          lastNumbers: data.last4 || "",
          last4: data.last4 || "",
          cardholderName: `${donorData?.first_name || ""} ${donorData?.last_name || ""}`.trim(),
          expiration: "",
        };

        const alreadyExists = existingCards.some((c) => String(c._id) === String(data.donorCardId));
        return {
          ...prev,
          cards: alreadyExists
            ? existingCards.map((c) => (String(c._id) === String(data.donorCardId) ? { ...c, ...updatedCard } : c))
            : [updatedCard, ...existingCards],
        };
      });
    }
  );

  const tokenizeAchMutation = useApiClientMutation(
    (body) => api_endpoints.tokenizeFundraiserACH(fundraiserToken, body, fundraiserSessionTokenRef.current),
    [],
    (data) => {
      setAchTokenized(true);
      setPaymentTokenId(data.paymentTokenId);
      setPaymentMethodId(data.donorBankId);
      setBankLast4(data.last4);
    }
  );

  const chargeMutation = useApiClientMutation(
    (body) => api_endpoints.processFundraiserCharge(fundraiserToken, body, fundraiserSessionTokenRef.current),
    [],
    (data) => {
      setTransactionId(data.transactionId);
      navigateTo(STEPS.SUCCESS);
    }
  );

  const subscribeMutation = useApiClientMutation(
    (body) => api_endpoints.createFundraiserSubscription(fundraiserToken, body, fundraiserSessionTokenRef.current),
    [],
    (data) => {
      setTransactionId(data.subscriptionId);
      navigateTo(STEPS.SUCCESS);
    }
  );

  // ========== Navigation Helpers ==========
  const navigateTo = useCallback((step) => {
    setStepHistory(prev => [...prev, step]);
    setCurrentStep(step);
  }, []);

  const resetFundraiserFlow = useCallback(
    ({ clearEmail = false } = {}) => {
      promisesRequestVersionRef.current += 1;
      otpVerificationVersionRef.current += 1;
      paymentIntentRef.current = { fingerprint: null, id: null };
      clearSavedState();
      sendOtpMutation.reset();
      verifyOtpMutation.reset();

      setCurrentStep(STEPS.EMAIL_VERIFICATION);
      setStepHistory([STEPS.EMAIL_VERIFICATION]);

      if (clearEmail) setEmail("");
      setOtp("");
      setOtpSent(false);
      setOtpVerified(false);
      clearFundraiserSession();
      setIsNewDonor(false);

      setDonorData(null);
      setFormData({
        first_name: "",
        last_name: "",
        phone: "",
        address: "",
        zip_code: "",
      });
      setSavedPaymentMethods({ cards: [], banks: [] });

      setPromiseData(null);
      setExistingPromises([]);
      setSelectedExistingPromise(null);
      setAmount("");
      setPaymentType("one_time");
      setFrequency("monthly");
      setStartDate("");
      setEndDate(null);
      setNumberOfPayments(null);
      setPerPaymentAmount(null);
      setSelectedCampaign(campaign?._id || "");
      setNotes("");
      setSurchargeEnabled(false);
      setPromiseOnly(false);

      setPaymentMethod("card");
      setSelectedPaymentMethodId(null);
      setAddingNewPaymentMethod(false);

      setCardTokenized(false);
      setCardTokenData(null);
      setPaymentTokenId(null);
      setPaymentMethodId(null);
      setCardLast4("");
      setCardBrand("");

      setAchTokenized(false);
      setBankName("");
      setBankLast4("");
      setAccountHolderName("");

      setTransactionId(null);
      setPromiseId(null);
      setSubmitError(null);

      setCloverReady(false);
      setCloverError("");
      setForteReady(false);
      setForteError("");
      cloverMounted.current = false;
    },
    [campaign?._id, clearFundraiserSession, sendOtpMutation, verifyOtpMutation]
  );

  const resetToEmailStep = useCallback(() => {
    resetFundraiserFlow();
  }, [resetFundraiserFlow]);

  const navigateBack = useCallback(() => {
    // Registration/profile sit behind a completed OTP verification. Going back
    // should restart verification, not reopen the verified OTP screen and then
    // auto-forward to the same step again.
    if (
      currentStep === STEPS.DONOR_REGISTRATION ||
      currentStep === STEPS.DONOR_PROFILE
    ) {
      resetToEmailStep();
      return;
    }

    if (stepHistory.length > 1) {
      const newHistory = [...stepHistory];
      newHistory.pop();
      setStepHistory(newHistory);
      setCurrentStep(newHistory[newHistory.length - 1]);
    }
  }, [currentStep, resetToEmailStep, stepHistory]);


  // ========== Get Steps for Progress Indicator ==========
  const getDisplaySteps = useMemo(() => {
    const steps = [
      { key: STEPS.EMAIL_VERIFICATION, label: t("verification", "Verification") },
    ];

    if (isNewDonor) {
      steps.push({ key: STEPS.DONOR_REGISTRATION, label: t("registration", "Registration") });
    } else {
      steps.push({ key: STEPS.DONOR_PROFILE, label: t("profile", "Profile") });
    }

    if (!isNewDonor && (existingPromises.length > 0 || currentStep === STEPS.PROMISE_SELECTION)) {
      steps.push({ key: STEPS.PROMISE_SELECTION, label: t("promises", "Promises") });
    }

    steps.push(
      selectedExistingPromise
        ? { key: STEPS.EXISTING_PROMISE_PAYMENT, label: t("promise", "Promise") }
        : { key: STEPS.PROMISE_FORM, label: t("donation", "Donation") }
    );

    if (!promiseOnly) {
      steps.push(
        { key: STEPS.PAYMENT_METHOD_SELECTION, label: t("payment", "Payment") },
        { key: STEPS.PREVIEW, label: t("confirm", "Confirm") },
      );
    }

    return steps;
  }, [isNewDonor, existingPromises.length, selectedExistingPromise, currentStep, promiseOnly, t]);

  const currentStepIndex = useMemo(() => {
    return getDisplaySteps.findIndex(s => s.key === currentStep);
  }, [currentStep, getDisplaySteps]);

  // ========== Initialize Clover SDK ==========
  useEffect(() => {
    if (currentStep !== STEPS.NEW_PAYMENT_METHOD || paymentMethod !== "card") return;

    let cancelled = false;

    const initClover = async () => {
      try {
        setCloverLoading(true);
        setCloverError("");
        await loadScript(cloverSdkUrl());
        if (cancelled) return;
        if (!window.Clover) throw new Error("missing-clover");

        const { data } = await api_endpoints.getFundraiserCloverPakms(fundraiserToken, organization._id, fundraiserSessionTokenRef.current);
        if (cancelled) return;
        const apiAccessKey = data?.apiAccessKey;
        if (!apiAccessKey) throw new Error("missing-key");

        const clover = new window.Clover(apiAccessKey);
        if (cancelled) return;
        cloverRef.current = clover;

        const elements = clover.elements();
        const styles = {
          body: { fontFamily: "Inter, system-ui, sans-serif", fontSize: "18px" },
          input: { fontSize: "18px", color: "#1f2937" },
        };
        cloverElements.current = {
          number: elements.create("CARD_NUMBER", styles),
          date: elements.create("CARD_DATE", styles),
          cvv: elements.create("CARD_CVV", styles),
          postal: elements.create("CARD_POSTAL_CODE", styles),
        };

        setCloverReady(true);
      } catch (error) {
        if (cancelled) return;
        console.error("[MobileForm] Clover init", error);
        const errorMessage = translateFundraiserError(t, error?.response?.data?.message);
        if (errorMessage === "Organization not configured for card payments") {
          setCardPaymentAvailable(false);
          setCloverError(t("card_not_configured", "Card payments are not available. Please use bank transfer."));
          setPaymentMethod("ach");
        } else {
          setCloverError(t("card_setup_error", "Failed to initialize card form. Please try again."));
        }
      } finally {
        if (!cancelled) setCloverLoading(false);
      }
    };

    initClover();
    return () => { cancelled = true; };
  }, [currentStep, paymentMethod, fundraiserToken, organization._id, fundraiserSessionToken, t]);

  // ========== Mount Clover Elements ==========
  useEffect(() => {
    if (!cloverReady || paymentMethod !== "card" || currentStep !== STEPS.NEW_PAYMENT_METHOD) return;
    const { number, date, cvv, postal } = cloverElements.current || {};
    if (!number || !date || !cvv || !postal) return;
    if (cloverMounted.current) return;

    const waitForElements = setInterval(() => {
      const numberEl = document.querySelector("#clover-card-number");
      const dateEl = document.querySelector("#clover-card-date");
      const cvvEl = document.querySelector("#clover-card-cvv");
      const postalEl = document.querySelector("#clover-card-postal");

      if (numberEl && dateEl && cvvEl && postalEl) {
        clearInterval(waitForElements);
        try {
          number.mount("#clover-card-number");
          date.mount("#clover-card-date");
          cvv.mount("#clover-card-cvv");
          postal.mount("#clover-card-postal");
          cloverMounted.current = true;
        } catch (e) {
          console.error("[MobileForm] Clover mount", e);
          setCloverError(t("card_mount_error", "Failed to display card form"));
        }
      }
    }, 100);

    return () => clearInterval(waitForElements);
  }, [cloverReady, paymentMethod, currentStep, t]);

  // ========== Initialize Forte SDK ==========
  useEffect(() => {
    if (currentStep !== STEPS.NEW_PAYMENT_METHOD || paymentMethod !== "ach") return;

    let cancelled = false;

    (async () => {
      try {
        setForteLoading(true);
        setForteError("");
        await loadScript(forteJsUrl());
        if (cancelled) return;
        const ok = typeof window.Forte !== "undefined" || typeof window.forte !== "undefined";
        if (!ok) throw new Error("missing-forte");
        setForteReady(true);
      } catch (error) {
        if (cancelled) return;
        console.error("[MobileForm] Forte init", error);
        setForteError(t("ach_setup_error", "Failed to initialize bank form"));
      } finally {
        if (!cancelled) setForteLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [currentStep, paymentMethod, t]);

  // ========== Forte Callbacks ==========
  const handleForteSuccess = useCallback(async (resp) => {
    try {
      tokenizeAchMutation.mutate({
        email,
        donorId: donorData?._id,
        forteToken: resp?.one_time_token || resp?.onetime_token,
        bankInfo: {
          bankName,
          firstName: accountHolderName.split(" ")[0],
          lastName: accountHolderName.split(" ").slice(1).join(" "),
          last4: resp?.last_4,
        },
      });
    } catch (error) {
      setForteError(error.message || t("ach_tokenization_failed", "Failed to save bank account"));
    }
  }, [email, donorData, bankName, accountHolderName, tokenizeAchMutation, t]);

  const handleForteError = useCallback((resp) => {
    setForteError(resp?.response_description || t("ach_tokenization_failed", "Bank verification failed"));
  }, [t]);

  // Set up Forte callbacks
  useEffect(() => {
    if (paymentMethod !== "ach" || currentStep !== STEPS.NEW_PAYMENT_METHOD) return;
    window.__forteSuccess = handleForteSuccess;
    window.__forteError = handleForteError;
    return () => {
      delete window.__forteSuccess;
      delete window.__forteError;
    };
  }, [paymentMethod, currentStep, handleForteSuccess, handleForteError]);

  // ========== Event Handlers ==========
  const handleSendOtp = () => {
    const normalizedEmail = normalizeDonorEmail(email);
    if (!EMAIL_REGEX.test(normalizedEmail)) return;

    setEmail(normalizedEmail);
    setOtp("");
    setOtpVerified(false);
    sendOtpMutation.mutate({ email: normalizedEmail });
  };

  const handleVerifyOtp = () => {
    const normalizedEmail = normalizeDonorEmail(email);
    const normalizedOtp = otp.trim();
    if (!EMAIL_REGEX.test(normalizedEmail) || !normalizedOtp) return;

    setEmail(normalizedEmail);
    const verificationVersion = ++otpVerificationVersionRef.current;
    verifyOtpMutation.mutate(
      { email: normalizedEmail, otp: normalizedOtp },
      {
        onSuccess: (data) => {
          if (verificationVersion !== otpVerificationVersionRef.current) return;
          applyVerifiedOtpResponse(data);
        },
      }
    );
  };

  const handleChangeEmail = () => {
    resetToEmailStep();
  };

  const handleOtpVerified = () => {
    if (isNewDonor) {
      navigateTo(STEPS.DONOR_REGISTRATION);
    } else {
      navigateTo(STEPS.DONOR_PROFILE);
    }
  };

  const handleRegistration = async (registrationData) => {
    try {
      await createDonorMutation.mutateAsync({
        email,
        ...registrationData,
      });
      // Registration is complete. Keep Profile as the meaningful previous step so
      // Back from the promise form never returns to a registration form that was
      // already submitted.
      setStepHistory([
        STEPS.EMAIL_VERIFICATION,
        STEPS.DONOR_PROFILE,
        STEPS.PROMISE_FORM,
      ]);
      setCurrentStep(STEPS.PROMISE_FORM);
    } catch (error) {
      console.error("Registration failed:", error);
    }
  };

  const handleProfileUpdate = async (profileData) => {
    try {
      await updateDonorMutation.mutateAsync({
        donorId: donorData?._id,
        ...profileData,
      });
    } catch (error) {
      console.error("Profile update failed:", error);
    }
  };

  const handleExistingPromiseSelect = (promise) => {
    setSelectedExistingPromise(promise);
    setPromiseData(promise);
    setPromiseId(promise._id);
    setPromiseOnly(false);
    navigateTo(STEPS.EXISTING_PROMISE_PAYMENT);
  };

  const handleCreateNewPromise = () => {
    setSelectedExistingPromise(null);
    setPromiseData(null);
    setPromiseId(null);
    setAmount("");
    setPaymentType("one_time");
    setFrequency("monthly");
    setStartDate("");
    setEndDate(null);
    setNumberOfPayments(null);
    setPerPaymentAmount(null);
    setSurchargeEnabled(false);
    setPromiseOnly(false);
    navigateTo(STEPS.PROMISE_FORM);
  };

  const handleProfileContinue = () => {
    const requestedPromise = existingPromises.find(
      (promise) => String(promise._id) === String(requestedPromiseId)
    );
    if (requestedPromise) {
      handleExistingPromiseSelect(requestedPromise);
      return;
    }
    if (existingPromises.length > 0) {
      navigateTo(STEPS.PROMISE_SELECTION);
      return;
    }
    navigateTo(STEPS.PROMISE_FORM);
  };

  const handleExistingPromiseContinue = (paymentSetup) => {
    setAmount(paymentSetup.amount);
    setPaymentType(paymentSetup.paymentType);
    setFrequency(paymentSetup.frequency);
    setStartDate(paymentSetup.startDate);
    setEndDate(paymentSetup.endDate || null);
    setNumberOfPayments(paymentSetup.numberOfPayments || null);
    setPerPaymentAmount(paymentSetup.perPaymentAmount || null);
    setSurchargeEnabled(false);
    setPromiseOnly(false);
    navigateTo(STEPS.PAYMENT_METHOD_SELECTION);
  };

  const handlePromiseSubmit = async (promiseFormData) => {
    try {
      setSelectedExistingPromise(null);
      await createPromiseMutation.mutateAsync({
        donorId: donorData?._id,
        campaignId: promiseFormData.campaignId || selectedCampaign,
        amount: promiseFormData.amount,
        paymentType: promiseFormData.paymentType,
        frequency: promiseFormData.frequency,
        startDate: promiseFormData.startDate,
        endDate: promiseFormData.endDate || null,
        numberOfPayments: promiseFormData.numberOfPayments || null,
        notes: promiseFormData.notes,
        surchargeEnabled: promiseFormData.surchargeEnabled,
      });

      setAmount(promiseFormData.amount);
      setPaymentType(promiseFormData.paymentType);
      setFrequency(promiseFormData.frequency);
      setStartDate(promiseFormData.startDate);
      setEndDate(promiseFormData.endDate || null);
      setNumberOfPayments(promiseFormData.numberOfPayments || null);
      setPerPaymentAmount(promiseFormData.perPaymentAmount || null);
      setNotes(promiseFormData.notes);
      setSurchargeEnabled(promiseFormData.surchargeEnabled);

      if (promiseFormData.promiseOnly) {
        setPromiseOnly(true);
        navigateTo(STEPS.SUCCESS);
      } else {
        navigateTo(STEPS.PAYMENT_METHOD_SELECTION);
      }
    } catch (error) {
      console.error("Promise creation failed:", error);
    }
  };

  const handlePaymentMethodSelection = (method) => {
    // ACH/bank selection is disabled for now (no PROD Forte environment).
    if (method?.type === "ach" || method?.type === "bank") {
      setSubmitError(t("ach_temporarily_unavailable", "Bank payment is temporarily unavailable. Please use card."));
      return;
    }

    if (method.isNew) {
      setAddingNewPaymentMethod(true);
      setPaymentMethod(method.type);
      navigateTo(STEPS.NEW_PAYMENT_METHOD);
    } else {
      setSelectedPaymentMethodId(method.id);
      setPaymentMethodId(method.id);
      setPaymentMethod(method.type);
      if (method.type === "card") {
        setCardLast4(method.last4);
        setCardBrand(method.brand);
      } else {
        setBankLast4(method.last4);
        setBankName(method.bankName);
      }
      navigateTo(STEPS.PREVIEW);
    }
  };

  const handleTokenizeCard = async () => {
    if (!cloverReady || !cloverRef.current) {
      setCloverError(t("card_not_ready", "Card form not ready"));
      return;
    }

    try {
      const result = await cloverRef.current.createToken();
      if (result?.errors) {
        const combined = Object.values(result.errors).join(" ");
        throw new Error(combined || "card_tokenization_failed");
      }

      const tokenValue = result?.token;
      if (!tokenValue) {
        setCloverError(t("missing_card_token", "Could not create card token"));
        return;
      }

      tokenizeCardMutation.mutate({
        email,
        donorId: donorData?._id,
        cloverToken: tokenValue,
        cardInfo: {
          brand: result?.card?.brand,
          first6: result?.card?.first6 || result?.card?.first6Digits || result?.card?.bin || "",
          last4: result?.card?.last4,
          exp_month: result?.card?.expMonth || result?.card?.exp_month || result?.card?.month || "",
          exp_year: result?.card?.expYear || result?.card?.exp_year || result?.card?.year || "",
          cardholderName:
            result?.card?.name ||
            result?.card?.cardholderName ||
            `${donorData?.first_name || ""} ${donorData?.last_name || ""}`.trim(),
        },
      });
    } catch (error) {
      setCloverError(error.message || t("card_tokenization_failed", "Failed to tokenize card"));
    }
  };

  const handleTokenizeAch = async (achData) => {
    setBankName(achData.bankName || "");
    setAccountHolderName(achData.accountHolderName || "");

    const ForteApi = window.Forte || window.forte;
    if (!ForteApi) {
      setForteError(t("forte_not_loaded", "Bank verification system not ready"));
      return;
    }

    ForteApi.createToken({
      api_login_id: process.env.REACT_APP_FORTE_API_LOGIN_ID,
      account_type: achData.accountType || "checking",
      routing_number: achData.routingNumber,
      account_number: achData.accountNumber,
      success_callback: "__forteSuccess",
      error_callback: "__forteError",
    });
  };

  const handleCardTokenized = () => {
    navigateTo(STEPS.PREVIEW);
  };

  const handleAchTokenized = () => {
    // ACH flow intentionally disabled.
    navigateTo(STEPS.PREVIEW);
  };

  useEffect(() => {
    if (cardTokenized && currentStep === STEPS.NEW_PAYMENT_METHOD) {
      handleCardTokenized();
    }
  }, [cardTokenized, currentStep]);

  useEffect(() => {
    if (achTokenized && currentStep === STEPS.NEW_PAYMENT_METHOD) {
      handleAchTokenized();
    }
  }, [achTokenized, currentStep]);

  const handlePaymentConfirm = async () => {
    try {
      setSubmitError(null);

      if (!promiseId) {
        setSubmitError(t("promise_missing", "Please create a promise before submitting payment."));
        return;
      }

      const paymentIntentFingerprint = JSON.stringify([
        promiseId,
        Number(amount),
        surchargeEnabled ? Number(surchargeValue || 0) : 0,
        paymentMethod,
        paymentMethodId,
      ]);
      if (
        paymentType === "one_time" &&
        paymentIntentRef.current.fingerprint !== paymentIntentFingerprint
      ) {
        paymentIntentRef.current = {
          fingerprint: paymentIntentFingerprint,
          id: createPaymentIntentId(),
        };
      }

      const payload = buildPaymentPayload({
        email,
        donorData,
        formData,
        amount,
        surchargeEnabled,
        surchargeValue,
        paymentMethod,
        paymentMethodId,
        paymentType,
        frequency,
        startDate,
        endDate,
        numberOfPayments,
        perPaymentAmount,
        billingAnchorDay: null,
        promiseId,
        paymentIntentId:
          paymentType === "one_time" ? paymentIntentRef.current.id : null,
      });

      if (paymentType === "split" || paymentType === "recurring") {
        subscribeMutation.mutate(payload);
      } else {
        chargeMutation.mutate(payload);
      }
    } catch (error) {
      setSubmitError(error.message || t("payment_failed", "Payment processing failed"));
    }
  };

  const handleNewDonation = async () => {
    paymentIntentRef.current = { fingerprint: null, id: null };
    const hadExistingPromise = Boolean(selectedExistingPromise);
    const wasPromiseOnly = promiseOnly;
    const shouldReturnToPromises = hadExistingPromise || wasPromiseOnly;
    const hadLoadedPromises = existingPromises.length > 0;
    const promisesRequestVersion = ++promisesRequestVersionRef.current;

    setAmount("");
    setPaymentType("one_time");
    setFrequency("monthly");
    setStartDate("");
    setEndDate(null);
    setNumberOfPayments(null);
    setPerPaymentAmount(null);
    setNotes("");
    setSurchargeEnabled(false);
    setPromiseOnly(false);
    setPromiseData(null);
    setPromiseId(null);
    setSelectedExistingPromise(null);
    setSelectedPaymentMethodId(null);
    setPaymentMethodId(null);
    setCardTokenized(false);
    setAchTokenized(false);
    setTransactionId(null);
    setSubmitError(null);
    cloverMounted.current = false;

    try {
      const response = await api_endpoints.getFundraiserPromises(
        fundraiserToken,
        fundraiserSessionTokenRef.current
      );
      if (promisesRequestVersion !== promisesRequestVersionRef.current) return;
      const refreshedPromises = Array.isArray(response.data) ? response.data : [];
      setExistingPromises(refreshedPromises);
      if (shouldReturnToPromises || refreshedPromises.length > 0) {
        setStepHistory([STEPS.PROMISE_SELECTION]);
        setCurrentStep(STEPS.PROMISE_SELECTION);
        return;
      }
    } catch (error) {
      if (promisesRequestVersion !== promisesRequestVersionRef.current) return;
      console.error("Failed to refresh promises:", error);
      if (requestedPromiseId || shouldReturnToPromises || hadLoadedPromises) {
        setSubmitError(
          t(
            "failed_to_refresh_promises",
            "We could not refresh your promises. Please try again."
          )
        );
        setStepHistory([STEPS.PROMISE_SELECTION]);
        setCurrentStep(STEPS.PROMISE_SELECTION);
        return;
      }
    }

    setStepHistory([STEPS.PROMISE_FORM]);
    setCurrentStep(STEPS.PROMISE_FORM);
  };

  const handleLogout = useCallback(() => {
    resetFundraiserFlow({ clearEmail: true });
  }, [resetFundraiserFlow]);

  useEffect(() => {
    setExpiryHandler(handleLogout);
    return () => setExpiryHandler(null);
  }, [handleLogout, setExpiryHandler]);

  useEffect(() => {
    const protectedMutationErrors = [
      createDonorMutation.error,
      updateDonorMutation.error,
      createPromiseMutation.error,
      tokenizeCardMutation.error,
      tokenizeAchMutation.error,
      chargeMutation.error,
      subscribeMutation.error,
    ];

    if (protectedMutationErrors.some((error) => error?.response?.status === 401)) {
      handleLogout();
    }
  }, [
    createDonorMutation.error,
    updateDonorMutation.error,
    createPromiseMutation.error,
    tokenizeCardMutation.error,
    tokenizeAchMutation.error,
    chargeMutation.error,
    subscribeMutation.error,
    handleLogout,
  ]);

  // ========== Render Step Content ==========
  const renderStepContent = () => {
    switch (currentStep) {
      case STEPS.EMAIL_VERIFICATION:
        return (
          <>
            {organization?.logo && (
              <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
                <img
                  src={organization.logo.startsWith("http") ? organization.logo : `${process.env.REACT_APP_S3_URL}${organization.logo}`}
                  alt="Organization Logo"
                  style={{ maxWidth: 120, maxHeight: 120, objectFit: "contain" }}
                />
              </Box>
            )}
            <EmailVerification
              t={t}
              email={email}
              setEmail={setEmail}
              otp={otp}
              setOtp={setOtp}
              otpSent={otpSent}
              otpVerified={otpVerified}
              isNewDonor={isNewDonor}
              donorData={donorData}
              onSendOtp={handleSendOtp}
              onVerifyOtp={handleVerifyOtp}
              onChangeEmail={handleChangeEmail}
              onContinue={handleOtpVerified}
              sendOtpLoading={sendOtpMutation.isPending}
              verifyOtpLoading={verifyOtpMutation.isPending}
              sendOtpError={translateFundraiserError(t, sendOtpMutation.error?.response?.data?.message)}
              verifyOtpError={translateFundraiserError(t, verifyOtpMutation.error?.response?.data?.message)}
              isMobile={true}
            />
          </>
        );

      case STEPS.DONOR_REGISTRATION:
        return (
          <DonorRegistration
            t={t}
            email={email}
            initialData={formData}
            onSubmit={handleRegistration}
            isLoading={createDonorMutation.isPending}
            error={translateFundraiserError(t, createDonorMutation.error?.response?.data?.message)}
            isMobile={true}
          />
        );

      case STEPS.DONOR_PROFILE:
        return (
          <DonorProfile
            t={t}
            donorData={donorData}
            onUpdate={handleProfileUpdate}
            onContinue={handleProfileContinue}
            isLoading={updateDonorMutation.isPending}
            error={translateFundraiserError(t, updateDonorMutation.error?.response?.data?.message)}
            isMobile={true}
          />
        );

      case STEPS.PROMISE_SELECTION:
        return (
          <ExistingPromises
            t={t}
            promises={existingPromises}
            requestedPromiseId={requestedPromiseId}
            onSelect={handleExistingPromiseSelect}
            onCreateNew={handleCreateNewPromise}
            error={submitError}
          />
        );

      case STEPS.EXISTING_PROMISE_PAYMENT:
        return (
          <ExistingPromisePayment
            t={t}
            promise={selectedExistingPromise}
            onContinue={handleExistingPromiseContinue}
          />
        );

      case STEPS.PROMISE_FORM:
        return (
          <PromiseForm
            t={t}
            organization={organization}
            campaigns={campaign ? [campaign] : []}
            defaultCampaignId={campaign._id}
            initialData={{
              amount,
              paymentType,
              frequency,
              startDate,
              notes,
              surchargeEnabled,
            }}
            onSubmit={handlePromiseSubmit}
            isLoading={createPromiseMutation.isPending}
            error={translateFundraiserError(t, createPromiseMutation.error?.response?.data?.message)}
            paymentMethod={paymentMethod}
            isMobile={true}
            acceptRecurring={Boolean(campaign?.is_fundraiser_accept_recurring)}
          />
        );

      case STEPS.PAYMENT_METHOD_SELECTION:
        return (
          <PaymentMethodsList
            t={t}
            cards={savedPaymentMethods.cards}
            banks={savedPaymentMethods.banks}
            onSelect={handlePaymentMethodSelection}
            cardPaymentAvailable={cardPaymentAvailable}
            achPaymentAvailable={achPaymentAvailable}
            isMobile={true}
          />
        );

      case STEPS.NEW_PAYMENT_METHOD:
        // ACH/bank payment method creation is disabled; keep card form only.
        if (paymentMethod === "card") {
          return (
            <CardPaymentForm
              t={t}
              cloverReady={cloverReady}
              cloverLoading={cloverLoading}
              cloverError={cloverError}
              onTokenize={handleTokenizeCard}
              isTokenizing={tokenizeCardMutation.isPending}
              tokenizeError={translateFundraiserError(t, tokenizeCardMutation.error?.response?.data?.message)}
              isMobile={true}
            />
          );
        }
        return (
          <Alert severity="warning">
            {t("ach_temporarily_unavailable", "Bank payment is temporarily unavailable. Please use card.")}
          </Alert>
        );

      case STEPS.PREVIEW:
        return (
          <PaymentPreview
            t={t}
            donorData={donorData}
            promiseData={promiseData}
            amount={amount}
            surchargeEnabled={surchargeEnabled}
            surchargeValue={surchargeValue}
            totalAmount={totalAmount}
            paymentMethod={paymentMethod}
            paymentMethodId={paymentMethodId}
            cardLast4={cardLast4}
            cardBrand={cardBrand}
            bankLast4={bankLast4}
            bankName={bankName}
            paymentType={paymentType}
            frequency={frequency}
            startDate={startDate}
            onConfirm={handlePaymentConfirm}
            onBack={navigateBack}
            isProcessing={chargeMutation.isPending || subscribeMutation.isPending}
            processError={submitError || translateFundraiserError(t, chargeMutation.error?.response?.data?.message) || translateFundraiserError(t, subscribeMutation.error?.response?.data?.message)}
            isMobile={true}
          />
        );

      case STEPS.SUCCESS:
        return (
          <DonationSuccess
            t={t}
            donorData={donorData}
            promiseData={promiseData}
            transactionId={transactionId}
            promiseId={promiseId}
            promiseOnly={promiseOnly}
            amount={amount}
            totalAmount={totalAmount}
            paymentType={paymentType}
            frequency={frequency}
            campaignName={campaign?.name}
            organizationName={organization?.legal_name}
            onNewDonation={handleNewDonation}
            onClose={handleLogout}
            returnToPromises={Boolean(selectedExistingPromise) || promiseOnly}
            isMobile={true}
          />
        );

      default:
        return null;
    }
  };

  const canGoBack = stepHistory.length > 1 && 
    currentStep !== STEPS.SUCCESS && 
    currentStep !== STEPS.EMAIL_VERIFICATION;

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        bgcolor: theme.palette.background.default,
        zIndex: 1300, // Above most MUI components
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          pb: 0,
          borderBottom: `1px solid ${theme.palette.divider}`,
          bgcolor: theme.palette.background.paper,
          position: "sticky",
          top: 0,
          zIndex: 10,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}>
          {canGoBack && (
            <IconButton onClick={navigateBack} sx={{ flexShrink: 0 }}>
              <ArrowBackIcon />
            </IconButton>
          )}
          <Typography
            variant="h6"
            sx={{
              flex: 1,
              minWidth: 0,
              width: "100%",
              px: "10px",
              pt: "8px",
              fontWeight: 600,
              fontSize: "1.1rem",
              lineHeight: 1.25,
              overflowWrap: "anywhere",
              wordBreak: "break-word",
            }}
          >
            {campaign?.name || t("donation", "Donation")}
          </Typography>
          {otpVerified && (
            <Button
              variant="contained"
              color="error"
              size="small"
              onClick={handleLogout}
              sx={{ flexShrink: 0, mt: "5px", textTransform: "none", fontWeight: 700 }}
            >
              {t("logout", "Logout")}
            </Button>
          )}
        </Box>
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <LanguageSwitcher />
        </Box>
      </Box>

      {/* Stepper - Compact for mobile */}
      {currentStep !== STEPS.SUCCESS && (
        <Box sx={{ px: 2, py: 1.5, bgcolor: theme.palette.background.paper }}>
          <Stepper 
            activeStep={currentStepIndex >= 0 ? currentStepIndex : 0} 
            alternativeLabel
            sx={{
              "& .MuiStepLabel-label": {
                fontSize: "0.7rem",
                mt: 0.5,
              },
              "& .MuiStepIcon-root": {
                width: 24,
                height: 24,
              },
            }}
          >
            {getDisplaySteps.slice(0, 4).map((step) => (
              <Step key={step.key}>
                <StepLabel>{step.label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>
      )}

      {/* Content */}
      <Box
        sx={{
          flex: 1,
          p: 2,
          overflowY: "auto",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 2,
            bgcolor: theme.palette.background.paper,
          }}
        >
            {renderStepContent()}
          </Paper>
      </Box>

      {/* Organization branding */}
      <Box
        sx={{
          p: 2,
          textAlign: "center",
          borderTop: `1px solid ${theme.palette.divider}`,
          bgcolor: theme.palette.background.paper,
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
          {t("powered_by", "Powered by")}
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <img
            src={`${process.env.PUBLIC_URL}/Kiosk-Donations.svg`}
            alt="Kiosk Donations"
            style={{ width: 120, objectFit: "contain" }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default MobileForm;
